import { wrapCreateRootRouteWithSentry } from '@sentry/tanstackstart-react';
import { QueryClient } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Scripts,
} from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import type { ReactNode } from 'react';

import { AuthenticationProvider } from '@/components/auth/AuthenticationProvider';
import { DebugModeShortcut } from '@/components/debug/DebugModeShortcut';
import { AlphaRedirectGuard } from '@/components/posthog/AlphaRedirectGuard';
import { PostHogProvider } from '@/components/posthog/PostHogProvider';
import { SentryContextProvider } from '@/components/sentry/SentryContextProvider';
import { oneRosterUserQueryOptions } from '@/hooks/useOneRosterAuthQuery';

import CSS from '../index.css?url';

export const Route = wrapCreateRootRouteWithSentry(
  createRootRouteWithContext<{
    queryClient: QueryClient;
  }>()
)({
  beforeLoad: async ({ context }) => {
    const queryClient = context.queryClient;
    const auth = await queryClient.fetchQuery(oneRosterUserQueryOptions());
    return { auth };
  },
  head: () => ({
    meta: [
      {
        charSet: 'utf-8',
      },
      {
        name: 'viewport',
        content: 'width=device-width, initial-scale=1',
      },
      {
        title: 'Timeback Platform',
      },
    ],
    links: [
      { rel: 'stylesheet', href: CSS },
      { rel: 'icon', type: 'image/png', href: '/favicon.png' },
      {
        rel: 'preload',
        href: '/fonts/Lexend-Light.ttf',
        as: 'font',
        type: 'font/ttf',
        crossOrigin: 'anonymous',
      },
      {
        rel: 'preload',
        href: '/fonts/Lexend-Regular.ttf',
        as: 'font',
        type: 'font/ttf',
        crossOrigin: 'anonymous',
      },
      {
        rel: 'preload',
        href: '/fonts/Lexend-SemiBold.ttf',
        as: 'font',
        type: 'font/ttf',
        crossOrigin: 'anonymous',
      },
      {
        rel: 'preload',
        href: '/fonts/Lexend-Bold.ttf',
        as: 'font',
        type: 'font/ttf',
        crossOrigin: 'anonymous',
      },
      {
        rel: 'preload',
        href: '/fonts/Lexend-ExtraBold.ttf',
        as: 'font',
        type: 'font/ttf',
        crossOrigin: 'anonymous',
      },
    ],
  }),
  component: RootComponent,
});

const enableRouterDevtools =
  import.meta.env.VITE_ENABLE_ROUTER_DEVTOOLS === 'true';
const enableDevTools = import.meta.env.VITE_ENABLE_DEV_TOOLS !== 'false';

function RootComponent() {
  return (
    <AuthenticationProvider>
      <SentryContextProvider>
        <PostHogProvider>
          <AlphaRedirectGuard />
          <RootDocument>
            <Outlet />
            {enableRouterDevtools && (
              <TanStackRouterDevtools position="top-right" />
            )}
            {enableDevTools && <ReactQueryDevtools initialIsOpen={false} />}
            <DebugModeShortcut />
          </RootDocument>
        </PostHogProvider>
      </SentryContextProvider>
    </AuthenticationProvider>
  );
}

function RootDocument({ children }: Readonly<{ children: ReactNode }>) {
  return (
    <html className="overscroll-y-none">
      <head>
        <HeadContent />
      </head>
      <body className="overscroll-y-none">
        {children}
        <Scripts />
      </body>
    </html>
  );
}
